import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Menu, X } from 'lucide-react';
import { ASSETS } from '@/data/brands';

const links = [
  { key: 'home', href: '#home' },
  { key: 'about', href: '#about' },
  { key: 'products', href: '#products' },
  { key: 'cases', href: '#cases' },
  { key: 'tips', href: '#tips' },
  { key: 'faq', href: '#faq' },
  { key: 'contact', href: '#contact' },
];

export default function Navbar({ onLangToggle }) {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? 'py-3' : 'py-5'
        }`}
      >
        <div
          className={`mx-4 md:mx-8 rounded-full transition-all duration-500 ${
            scrolled ? 'dwk-glass shadow-[0_10px_40px_-20px_rgba(16,9,46,0.25)]' : 'bg-transparent'
          }`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-2.5">
            {/* Logo */}
            <a href="#home" className="flex items-center gap-3 group" data-testid="brand-logo">
              <div
                className={`inline-flex items-center justify-center rounded-full transition-all duration-300 ${
                  scrolled ? 'bg-[#10092E] px-4 py-2 shadow-[0_10px_30px_-12px_rgba(137,72,195,0.5)]' : 'px-0 py-0'
                }`}
              >
                <img
                  src={ASSETS.logoWhite}
                  alt="Dowakum"
                  className={`w-auto object-contain transition-all duration-300 ${
                    scrolled ? 'h-16 md:h-20' : 'h-24 md:h-28'
                  } drop-shadow-[0_4px_12px_rgba(137,72,195,0.35)]`}
                />
              </div>
            </a>

            {/* Links */}
            <nav className="hidden lg:flex items-center gap-8">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  data-testid={`nav-${l.key}`}
                  className={`text-sm font-medium transition-colors ${
                    scrolled ? 'text-[#10092E]/80 hover:text-[#8948C3]' : 'text-white/85 hover:text-white'
                  }`}
                >
                  {t(`nav.${l.key}`)}
                </a>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <button
                data-testid="lang-switch"
                onClick={onLangToggle}
                aria-label="Toggle language"
                className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium backdrop-blur-xl transition-all duration-300 ${
                  scrolled
                    ? 'border-[#10092E]/15 bg-white/70 text-[#10092E] hover:border-[#8948C3] hover:text-[#8948C3]'
                    : 'border-white/25 bg-white/10 text-white hover:border-[#40D3BE] hover:text-[#40D3BE]'
                }`}
              >
                <Globe size={16} className="opacity-80 group-hover:rotate-12 transition-transform" />
                <span>{t('nav.switch')}</span>
              </button>

              <button
                data-testid="menu-toggle"
                className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border ${
                  scrolled ? 'border-[#10092E]/15 text-[#10092E]' : 'border-white/25 text-white'
                }`}
                onClick={() => setOpen((v) => !v)}
                aria-label="Menu"
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-[#10092E]/85 backdrop-blur-2xl" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute inset-x-4 top-24 rounded-3xl dwk-glass-dark p-6"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <nav className="flex flex-col gap-2">
                {links.map((l, i) => (
                  <motion.a
                    key={l.key}
                    href={l.href}
                    data-testid={`mobile-nav-${l.key}`}
                    onClick={() => setOpen(false)}
                    className="block text-white text-2xl font-semibold py-2 border-b border-white/10"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    {t(`nav.${l.key}`)}
                  </motion.a>
                ))}
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
